import {
  Box,
  Card,
  CardContent,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme
} from "@mui/material";

// Skeleton for user info card
export const userInfoCardSkeleton = () => {
  return (
    <Card
      sx={{
        height: "130px",
        borderRadius: "10px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ pr: 2,  pl: 2,  p: 1 }}   spacing={2}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Stack>
            <Skeleton variant="text" width={60} height={32} sx={{ bgcolor: "grey.500" }} />
            <Skeleton variant="text" width={180} height={32} sx={{ bgcolor: "grey.500" }} />
          </Stack>
          <Skeleton variant="circular" width={56} height={56} sx={{ bgcolor: "grey.500" }} />
        </Stack>
        <Stack sx={{ marginTop: "10px",  justifyContent: "space-between", alignItems: "center" }} direction="row" >
          <Stack sx={{ alignItems: "center" }} spacing={1} direction="row">
            <Skeleton variant="text" width={40} height={32} sx={{ bgcolor: "grey.500" }} />
            <Skeleton variant="text" width={80} height={24} sx={{ bgcolor: "grey.500" }} />
          </Stack>
          <Skeleton
            variant="rectangular"
            width={120}
            height={30}
            sx={{ borderRadius: "7px", bgcolor: "grey.500" }}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

// Skeleton for build id info card
export const buildIdInfoCardSkeleton = () => {
  return (
    <Card
      sx={{
        height: "130px",
        cursor: "pointer",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ p: 1.5, alignItems: "center" }} spacing={1} direction={"row"}>
        <Skeleton variant="circular" width={48} height={40} sx={{ bgcolor: "grey.500" }} />
        <Skeleton variant="text" width={800} height={40} sx={{ bgcolor: "grey.500" }} />
      </Stack>
    </Card>
  );
};

export const questionsSkeleton = () => {
  return (
    <Card
      sx={{
        minHeight: 220,
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="rectangular" width="100%" height={40} />
          <Skeleton variant="text" width="40%" height={24} />
        </Stack>
      </CardContent>
    </Card>
  );
};

// Skeleton for Ads Carousel card
export const AdsCarouselSkeleton = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Card
      sx={{
        height: isSmallScreen ? "230px" : "250px",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent:'center',
            alignItems:'center',
          }}>
            <Card
              key={1}
              sx={{
                width: isSmallScreen ? 180 : 230,
                flex: "0 0 auto",
                borderRadius: "14px",
                border: "1px solid #e2e8f0"
              }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={isSmallScreen ? 140 : 165}
                sx={{ bgcolor: "grey.500" }}
              />
            </Card>
        </Box>
      </Stack>
    </Card>
  );
};

// Skeleton for site card
export const SiteCardSkeleton = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Card
      sx={{
        height: isSmallScreen ? "280px" : "330px",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ p: 3 }} spacing={2.5}>
        <Skeleton variant="text" width={180} height={32} sx={{ bgcolor: "grey.500" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2.5
          }}>
          {[1, 2, 3, 4, 5].map((_, idx) => (
            <Card
              key={idx}
              sx={{
                width: isSmallScreen ? 180 : 260,
                flex: "0 0 auto",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
                overflow: "hidden"
              }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={isSmallScreen ? 100 : 130}
                sx={{ bgcolor: "grey.500" }}
              />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="80%" height={24} sx={{ bgcolor: "grey.500" }} />
                <Skeleton variant="text" width="60%" height={20} sx={{ bgcolor: "grey.500" }} />
                <Skeleton variant="text" width="40%" height={20} sx={{ bgcolor: "grey.500" }} />
              </Box>
            </Card>
          ))}
        </Box>
      </Stack>
    </Card>
  );
};

// Skeleton for TimesheetCard
export const timesheetCardSkeleton = () => {
  return (
    <Card
      sx={{
        height: "350px",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ p: 2, flexGrow: 1, flexBasis: 0 }} spacing={1}>
        <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
          <Skeleton variant="text" width={130} height={36} sx={{ bgcolor: "grey.300" }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: "grey.300" }} />
          </Stack>
        </Stack>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
          <Stack spacing={1}>
            {[1, 2, 3].map((_, idx) => (
              <Card key={idx} sx={{ borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.01)" }}>
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemText primary={<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                          <Skeleton variant="text" width={120} height={24} sx={{ bgcolor: "grey.300" }} />
                          <Stack direction="row" spacing={2}>
                            <Stack sx={{ alignItems: "center" }}>
                              <Skeleton variant="text" width={40} height={18} sx={{ bgcolor: "grey.300" }} />
                              <Skeleton variant="text" width={30} height={18} sx={{ bgcolor: "grey.300" }} />
                            </Stack>
                            <Stack sx={{ alignItems: "center" }}>
                              <Skeleton variant="text" width={40} height={18} sx={{ bgcolor: "grey.300" }} />
                              <Skeleton variant="text" width={50} height={18} sx={{ bgcolor: "grey.300" }} />
                            </Stack>
                          </Stack>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </Card>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

// Skeleton for ExpensesCard
export const expensesCardSkeleton = () => {
  return (
    <Card
      sx={{
        height: "350px",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.03)",
        border: "1px solid #f1f5f9"
      }}
    >
      <Stack sx={{ p: 2 }} spacing={1}>
        <Stack sx={{ justifyContent: "space-between", alignItems: "center" }} direction="row">
          <Skeleton variant="text" width={120} height={32} sx={{ bgcolor: "grey.500" }} />
          <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: "grey.500" }} />
        </Stack>
        <Stack spacing={1.5}>
          {[1, 2, 3].map((_, idx) => (
            <Card key={idx} sx={{ borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "0 2px 8px rgba(0,0,0,0.01)" }}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemText
                    primary={
                      <Skeleton
                        variant="text"
                        width={120}
                        height={24}
                        sx={{ bgcolor: "grey.500" }}
                      />
                    }
                    secondary={
                      <Skeleton
                        variant="text"
                        width={80}
                        height={15}
                        sx={{ bgcolor: "grey.500" }}
                      />
                    }
                  />
                  <Skeleton
                    variant="text"
                    width={60}
                    height={24}
                    sx={{ bgcolor: "grey.500", ml: 2 }}
                  />
                </ListItemButton>
              </ListItem>
            </Card>
          ))}
          <Box sx={{ mt: 2,  justifyContent: "center",  display: "flex" }}  >
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: "15px", bgcolor: "grey.500" }}
            />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
};

// Skeleton for CompanyList from Home
export const CompanyListSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      <Stack sx={{ display: "flex",  justifyContent: "space-between", alignItems: "center" }}  direction={"row"}>
        <Skeleton variant="text" width={100} height={28} />
        <Skeleton variant="rectangular" width={120} height={36} />
      </Stack>
      <Stack spacing={1}>
        {[...Array(3)].map((_, idx) => (
          <Card key={idx} className="pagelist-content">
            <ListItem style={{ padding: "0px" }}>
              <ListItemButton>
                <Skeleton variant="text" width="80%" height={24} />
              </ListItemButton>
              {/* Three-dot skeleton */}
              <Stack sx={{ alignItems: "center", ml: 1, mx: 2 }} direction="column" spacing={0.5}>
                <Skeleton variant="circular" width={6} height={6} />
                <Skeleton variant="circular" width={6} height={6} />
                <Skeleton variant="circular" width={6} height={6} />
              </Stack>
            </ListItem>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};

// Skeleton for CompaniesSkeleton
export const CompaniesSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      {[...Array(11)].map((_, idx) => (
        <Card key={idx} className="pagelist-content">
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemText primary={<Skeleton variant="text" width="99%" height={30} />} />
            </ListItemButton>
          </ListItem>
        </Card>
      ))}
    </Stack>
  );
};

// Skeleton for ProductsSkeleton
export const ProductsSkeleton = () => {
  return (
    <Stack sx={{ gap: 1 }} >
      {[...Array(11)].map((_, idx) => (
        <Card key={idx} className="pagelist-content">
          <ListItem disablePadding>
            <ListItemButton>
              <ListItemText
                primary={<Skeleton variant="text" width="99%" height={28} />}
                secondary={<Skeleton variant="text" width="30%" height={20} />}
              />
            </ListItemButton>
          </ListItem>
        </Card>
      ))}
    </Stack>
  );
};

